<template>
    <div v-if="available" class="user-search-card-highlights">
        <div class="user-search-card-highlights__list">
            <div
                v-for="(highlight, highlightIndex) in prepared"
                :key="`${user.id}_${highlightIndex}`"
                class="user-search-card-highlight-item"
            >
                <div class="user-search-card-highlight-item__title">{{ highlight.title }}</div>
                <div
                    class="user-search-card-highlight-item__list"
                    :class="{
                        'user-search-card-highlight-item__list--with-gap': highlight.gap,
                    }"
                >
                    <template v-if="openedFields[highlight.field] || !highlight.showMore">
                        <div
                            v-for="(text, index) in highlight.full"
                            :key="`${user.id}_${highlightIndex}_${index}_full`"
                            v-html="text"
                        />
                    </template>
                    <template v-else>
                        <div
                            v-for="(text, index) in highlight.min"
                            :key="`${user.id}_${highlightIndex}_${index}_min`"
                            v-html="text"
                        />
                    </template>
                </div>
                <div v-if="highlight.showMore" class="user-search-card-highlight-item__actions">
                    <span @click="toggleField(highlight.field)">
                        {{ openedFields[highlight.field] ? 'Скрыть' : 'Читать еще' }}
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    compareHighlightedSnippet,
    getUniqueHighlights,
    highlightStrKeywords,
    isStrContainKeywords,
} from '@/tools/graphQueryHelpers';

const ALLOWED_HIGHLIGHT_FIELDS = [
    'profile.about',
    'career.organization',
    'career.position',
    'career.responsibilities',
    'education.education_place',
    'education.foreign_place',
    'education.faculty',
    'education.specialty_special',
    'comments.comment',
];

export default {
    name: 'UserSearchCardHighlights',

    props: {
        user: {
            type: Object,
            default: () => {},
        },
        opened: {
            type: Boolean,
            default: () => true,
        },
    },

    data: () => ({
        openedFields: {
            profile: false,
            career: false,
            education: false,
            comments: false,
        },
    }),

    watch: {
        user() {
            for (const field in this.openedFields) {
                this.openedFields[field] = false;
            }
        },
    },

    computed: {
        list() {
            return (this.user.highlights || []).filter(
                (highlight) => ALLOWED_HIGHLIGHT_FIELDS.indexOf(highlight.field) > -1
            );
        },

        prepared() {
            const result = [];
            const list = this.list;

            const profileAbout = list.filter((h) => h.field === 'profile.about');
            const careers = list.filter((h) => h.field.startsWith('career.'));
            const educations = list.filter((h) => h.field.startsWith('education.'));
            const comments = list.filter((h) => h.field === 'comments.comment');

            if (profileAbout.length) {
                const highlights = getUniqueHighlights(profileAbout);

                result.push({
                    field: 'profile',
                    title: 'О себе',
                    min: profileAbout.map((v) => v.highlights).flat(),
                    full: [
                        highlightStrKeywords(this.user.profile.about, highlights.keywords).replaceAll(
                            /(\r\n|\n|\r)/g,
                            '<br/>'
                        ),
                    ],
                    showMore:
                        this.user.profile.about.length >= 200 || highlights.group['profile.about'].items.length > 1,
                    gap: false,
                });
            }

            if (careers.length) {
                const highlights = getUniqueHighlights(careers);
                let showMore = false;

                const matchedCareers = this.user.career
                    .map((career) => {
                        let organization = career.organization || '';
                        let position = career.position || '';
                        let responsibilities = career.responsibilities || '';
                        let responsibilitiesHide = this.truncate(career.responsibilities || '', 200);
                        const showMoreCurrent = responsibilities.length >= 200;

                        if (showMoreCurrent) {
                            showMore = true;
                        }

                        let matched = false;

                        if (
                            highlights.group['career.organization'] &&
                            isStrContainKeywords(organization, highlights.group['career.organization'].keywords)
                        ) {
                            organization = highlightStrKeywords(
                                organization,
                                highlights.group['career.organization'].keywords
                            );
                            matched = true;
                        }
                        if (
                            highlights.group['career.position'] &&
                            isStrContainKeywords(position, highlights.group['career.position'].keywords)
                        ) {
                            position = highlightStrKeywords(position, highlights.group['career.position'].keywords);
                            matched = true;
                        }

                        if (highlights.group['career.responsibilities']) {
                            const responsibilitiesHideArr = [];
                            const keywords = [];

                            highlights.group['career.responsibilities'].items.forEach((item) => {
                                if (compareHighlightedSnippet(responsibilities, item.text)) {
                                    responsibilitiesHideArr.push(`${item.text}...`);
                                    keywords.push(...item.keywords);
                                }
                            });

                            if (responsibilitiesHideArr.length > 0) {
                                responsibilities = highlightStrKeywords(responsibilities, new Set(keywords));
                                responsibilitiesHide = showMoreCurrent
                                    ? responsibilitiesHideArr.join('<br/>')
                                    : responsibilities;
                                matched = true;
                            }
                        }

                        return matched
                            ? {
                                  organization,
                                  position,
                                  responsibilities: responsibilities.replaceAll(/(\r\n|\n|\r)/g, '<br/>'),
                                  responsibilitiesHide,
                              }
                            : null;
                    })
                    .filter((v) => v !== null);

                result.push({
                    field: 'career',
                    title: 'Опыт работы',
                    highlights,
                    min: matchedCareers.map((v) =>
                        [[v.organization, v.position].filter((v) => v.trim()).join(' - '), v.responsibilitiesHide]
                            .filter((v) => v.trim())
                            .join('<br/>')
                    ),
                    full: matchedCareers.map((v) =>
                        [[v.organization, v.position].filter((v) => v.trim()).join(' - '), v.responsibilities]
                            .filter((v) => v.trim())
                            .join('<br/>')
                    ),
                    showMore,
                    gap: true,
                });
            }

            if (educations.length) {
                const highlights = getUniqueHighlights(educations);

                const placeKeywords =
                    highlights.group['education.education_place'] || highlights.group['education.foreign_place']
                        ? new Set([
                              ...((highlights.group['education.education_place'] || {}).keywords || []),
                              ...((highlights.group['education.foreign_place'] || {}).keywords || []),
                          ])
                        : null;
                const matchedEducations = this.user.education
                    .map((education) => {
                        let place = education.education_place || education.foreign_place || '';
                        let faculty = education.faculty || '';
                        let specialty = education.specialty_special || '';

                        let matched = false;

                        if (placeKeywords && isStrContainKeywords(place, placeKeywords)) {
                            place = highlightStrKeywords(place, placeKeywords);
                            matched = true;
                        }
                        if (
                            highlights.group['education.faculty'] &&
                            isStrContainKeywords(faculty, highlights.group['education.faculty'].keywords)
                        ) {
                            faculty = highlightStrKeywords(faculty, highlights.group['education.faculty'].keywords);
                            matched = true;
                        }
                        if (
                            highlights.group['education.specialty_special'] &&
                            isStrContainKeywords(specialty, highlights.group['education.specialty_special'].keywords)
                        ) {
                            specialty = highlightStrKeywords(
                                specialty,
                                highlights.group['education.specialty_special'].keywords
                            );
                            matched = true;
                        }

                        return matched
                            ? {
                                  place,
                                  faculty,
                                  specialty,
                              }
                            : null;
                    })
                    .filter((v) => v !== null);

                const snippets = matchedEducations.map((v) =>
                    [[v.place, v.faculty].filter((v) => v.trim()).join(' - '), v.specialty]
                        .filter((v) => v.trim())
                        .join('<br/>')
                );

                result.push({
                    field: 'education',
                    title: 'Образование',
                    highlights,
                    min: snippets,
                    full: snippets,
                    showMore: false,
                    gap: true,
                });
            }

            if (comments.length) {
                const highlights = getUniqueHighlights(comments);

                const commentsArr = this.user.comments
                    .filter((v) => isStrContainKeywords(v.comment, highlights.keywords))
                    .map((v) => highlightStrKeywords(v.comment, highlights.keywords));

                result.push({
                    field: 'comments',
                    title: 'Комментарий администратора',
                    min: commentsArr,
                    full: commentsArr,
                    showMore: false,
                    gap: true,
                });
            }

            return result;
        },

        available() {
            return this.list.length;
        },
    },

    methods: {
        closeAll() {
            for (const field in this.openedFields) {
                this.openedFields[field] = false;
            }
        },
        toggleField(field) {
            this.openedFields[field] = !this.openedFields[field];
        },

        truncate(text, length, suffix = '...') {
            return text.length > length ? text.substring(0, length) + suffix : text;
        },
    },
};
</script>

<style scoped lang="scss">
@import '~@/styles/rem';
@import '~@/styles/variables';

.user-search-card-highlights {
    display: flex;
    flex-direction: column;
    gap: mrem(16);

    @include on-desktop {
        gap: rem(16);
    }

    &__list {
        display: flex;
        flex-direction: column;
        gap: mrem(16);
        padding: mrem(16) mrem(12);
        border-top: mrem(2) solid $color-main-notice-light;

        @include on-desktop {
            gap: rem(16);
            padding: rem(16) rem(12);
            border-width: rem(2);
        }
    }

    &__actions {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        color: $color-main-blue;
        font-style: normal;
        font-weight: 400;
        font-size: mrem(16);
        line-height: mrem(20);

        @include on-desktop {
            font-size: rem(16);
            line-height: rem(20);
        }

        span {
            cursor: pointer;
        }
    }
}

.user-search-card-highlight-item {
    font-style: normal;
    font-weight: 400;

    &__title {
        color: $color-main-grey;
        font-size: mrem(14);
        line-height: mrem(26);

        @include on-desktop {
            font-size: rem(14);
            line-height: rem(26);
        }
    }
    &__list {
        display: flex;
        flex-direction: column;

        color: $color-black-text;
        font-size: mrem(16);
        line-height: mrem(26);

        @include on-desktop {
            font-size: rem(16);
            line-height: rem(26);
        }

        &--with-gap {
            gap: mrem(8);

            @include on-desktop {
                gap: rem(8);
            }
        }

        :deep(.highlight) {
            color: $color-main-blue;
            font-weight: 600;
        }
    }
    &__actions {
        display: flex;
        flex-direction: column;
        align-items: flex-end;

        color: $color-main-grey;
        font-size: mrem(16);
        line-height: mrem(26);

        @include on-desktop {
            font-size: rem(16);
            line-height: rem(26);
        }

        span {
            cursor: pointer;
        }
    }
}
</style>
